/* eslint-disable react-perf/jsx-no-new-array-as-prop */

import '@cbhq/cds-web/globalStyles';

import { ReactElement, StrictMode, useCallback, useEffect, useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import { Redirect, Route, Router, Switch as RouterSwitch } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { zIndex } from '@cbhq/cds-common/tokens/zIndex';
import { Box, Divider, HStack, Spacer, VStack } from '@cbhq/cds-web/layout';
import { ClientKillSwitchProvider, defaultClientKillSwitchConfig } from '@cbhq/client-kill-switch';
import { ClientContextProvider as CBErrorVitalsClientContextProvider } from '@cbhq/error-vitals-web';
import { getIsLocalDev } from '@cbhq/session-manager-auth';
import { TwoFactorGlobalConfigProvider } from '@cbhq/two-factor-web';
import { TwoFactorBoundaryError, TwoFactorTargetEnv } from '@cbhq/two-factor-web/types';
import { triggerResearchEventOnUserAction } from '@cbhq/user-research-web';

import config from ':cloud/config';
import { AppStateProvider, useAppState } from ':cloud/contexts/AppStateContext';
import { ExperimentsProvider } from ':cloud/contexts/ExperimentsProvider';
import FeatureFlagProvider from ':cloud/contexts/FeatureFlagProvider';
import LegacyTokensProvider from ':cloud/contexts/LegacyTokensProvider';
import NavigationProvider from ':cloud/contexts/NavigationProvider';
import NewClusterProvider from ':cloud/contexts/NewClusterProvider';
import OAuthInfoProvider from ':cloud/contexts/OAuthInfoProvider';
import { ProjectProvider } from ':cloud/contexts/ProjectContext';
import { useBOAT } from ':cloud/hooks/featureFlags/useBOAT';
import { useIsBaseL3Enabled } from ':cloud/hooks/featureFlags/useIsBaseL3Enabled';
import { useIsBuildQuestEnabled } from ':cloud/hooks/featureFlags/useIsBuildQuestEnabled';
import { useIsLegacyAPITokenManagementEnabled } from ':cloud/hooks/featureFlags/useIsLegacyAPITokenManagementEnabled';
import { useIsOnParticipateAllowlist } from ':cloud/hooks/featureFlags/useIsOnParticipateAllowlist';
import { useIsOnStakingAllowlist } from ':cloud/hooks/featureFlags/useIsOnStakingAllowlist';
import { useIsWalletHistoryEnabled } from ':cloud/hooks/featureFlags/useIsWalletHistoryEnabled';
import useLocalStorage from ':cloud/hooks/useLocalStorage';
import useProjectNavigation from ':cloud/hooks/useProjectNavigation';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { errorVitalsClientContext } from ':cloud/init/bugsnag/init';
import { logError } from ':cloud/init/bugsnag/logging';
import { initKillSwitch } from ':cloud/init/killSwitch/init';
import { initSprig } from ':cloud/init/sprig/init';
import { AdvancedTradeLanding } from ':cloud/pages/AdvancedTradeLanding';
import { BaseL3Deployment } from ':cloud/pages/BaseL3Deployment';
import { BaseL3Landing } from ':cloud/pages/BaseL3Landing';
import { BaseLanding } from ':cloud/pages/BaseLanding';
import { BaseNodeTermsOfService } from ':cloud/pages/BaseNodeTermsOfService';
import { BuildQuestLanding } from ':cloud/pages/BuildQuest/BuildQuestLanding';
import { BundlerAndPaymasterLanding } from ':cloud/pages/BundlerAndPaymaster';
import { ClusterDetailsRoute } from ':cloud/pages/ClusterDetailsRoute';
import { CoinbaseAppLanding } from ':cloud/pages/CoinbaseAppLanding';
import { CommerceLanding } from ':cloud/pages/CommerceLanding';
import { ExchangeLanding } from ':cloud/pages/Exchange/ExchangeLanding';
import { Faucet } from ':cloud/pages/Faucet';
import { LegacyAPITokenManagement } from ':cloud/pages/LegacyAPITokenManagement';
import { OAuthCreateClient } from ':cloud/pages/OAuthCreateClient';
import OAuthEditClient from ':cloud/pages/OAuthEditClient';
import { OnChainData } from ':cloud/pages/OnChainData';
import { OnrampLanding } from ':cloud/pages/Onramp/OnrampLanding';
import { ParticipateDashboard } from ':cloud/pages/ParticipateDashboard';
import { PermissionsPage } from ':cloud/pages/Permissions';
import { PlatformWallets } from ':cloud/pages/PlatformWallets';
import { Projects } from ':cloud/pages/Projects';
import { Staking } from ':cloud/pages/Staking/Staking';
import { TemplateLanding } from ':cloud/pages/Template';
import { VerificationsLanding } from ':cloud/pages/Verifications/VerificationsLanding';
import { WalletFunding } from ':cloud/pages/WalletFunding';
import { WalletsLanding } from ':cloud/pages/WalletsLanding';
import { useGetAccount } from ':cloud/queries/BillingQueries/useGetAccount';
import { useIsKillSwitchLoaded } from ':cloud/queries/KillSwitchQueries/useIsKillSwitchLoaded';
import { useGetAllOrganizations } from ':cloud/queries/OrganizationQueries/useGetAllOrganizations';
import { useGetProjects } from ':cloud/queries/Projects/useGetProjects';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { useGetUserPermissions } from ':cloud/queries/UserQueries/useGetUserPermissions';
import { CloudThemeProvider } from ':cloud/utils/CloudThemeProvider';
import { CookieProvider } from ':cloud/utils/CookieProvider';
import { getDaysSinceDate } from ':cloud/utils/date';
import { history } from ':cloud/utils/history';
import { AppRoute, useQueryParam } from ':cloud/utils/routes';
import { SessionProvider } from ':cloud/utils/SessionProvider';
import { CreateCluster } from ':cloud/widgets/clusters/CreateCluster';
import { AppHelmet } from ':cloud/widgets/global/AppHelmet';
import { ADVANCED_API_REFERRER, REFERRER_KEY } from ':cloud/widgets/global/constants';
import { EnvBanner } from ':cloud/widgets/global/EnvBanner';
import { QueryClientProviderWrapper } from ':cloud/widgets/global/QueryClientProviderWrapper';
import { LeftNav } from ':cloud/widgets/navigation/LeftNav';
import { TopNav } from ':cloud/widgets/navigation/TopNav';
import { DiscordBanner } from ':cloud/widgets/product/DiscordBanner';
import { HiringBanner } from ':cloud/widgets/product/HiringBanner';
import { CloudErrorBoundary } from ':cloud/widgets/sharedcomponents/CloudErrorBoundary';
import { FullAppLoadingSpinner } from ':cloud/widgets/sharedcomponents/FullAppLoadingSpinner';
import MaintenanceBanner from ':cloud/widgets/sharedcomponents/MaintenanceBanner';

import { UnauthenticatedApp } from './UnauthenticatedApp';

function getTwoFactorTargetEnv(): TwoFactorTargetEnv {
  if (getIsLocalDev()) {
    return TwoFactorTargetEnv.Mock;
  }
  switch (process.env.AUTH_ENVIRONMENT) {
    case 'development':
      return TwoFactorTargetEnv.Development;
    case 'staging':
      return TwoFactorTargetEnv.Staging;
    case 'production':
    default:
      return TwoFactorTargetEnv.Production;
  }
}

function AppRouter(): ReactElement {
  const { isDarkMode, toggleDarkMode } = useAppState();
  const referrerQueryParam = useQueryParam(REFERRER_KEY);
  useEffect(() => {
    if (referrerQueryParam === ADVANCED_API_REFERRER && !isDarkMode) {
      toggleDarkMode();
    }
  }, [isDarkMode, referrerQueryParam, toggleDarkMode]);

  const { activeOrg, isLoading: isUserLoading } = useGetUser();
  const { isLoading: isKillSwitchLoading } = useIsKillSwitchLoaded();
  const { isLoading: isPermissionsLoading } = useGetUserPermissions(activeOrg?.organizationId);
  const { isLoading: isAccountLoading } = useGetAccount(activeOrg?.organizationId);
  const { isLoading: isProjectsLoading } = useGetProjects(activeOrg?.organizationId);
  const { isLoading: isOrganizationsLoading } = useGetAllOrganizations();

  const [hasClosedDiscordBanner] = useLocalStorage('has-closed-discord-banner', false);

  const { isPhone } = useSimpleBreakpoints();

  const isOnStakingAllowlist = useIsOnStakingAllowlist();
  const isOnParticipateAllowlist = useIsOnParticipateAllowlist();
  const isOnBOATAllowlist = useBOAT();
  const isWalletHistoryEnabled = useIsWalletHistoryEnabled();
  const isLegacyAPIManagementEnabled = useIsLegacyAPITokenManagementEnabled();
  const isBaseL3Enabled = useIsBaseL3Enabled();

  const shouldShowBanner = useMemo(() => {
    const currentDate = new Date();
    const maintenanceEndTime = new Date('2024-10-26T10:30:00-07:00'); // 10:30 AM PT

    return currentDate < maintenanceEndTime;
  }, []);

  useProjectNavigation();

  /** check for access to activeOrg, currentUser, user permissions, experiments and organizations top level */
  /** page level checks are not required */
  if (
    isUserLoading ||
    isKillSwitchLoading ||
    isAccountLoading ||
    isPermissionsLoading ||
    isOrganizationsLoading ||
    isProjectsLoading
  ) {
    return <FullAppLoadingSpinner />;
  }

  return (
    <>
      {shouldShowBanner && <MaintenanceBanner />}
      <NavigationProvider>
        <AppHelmet />
        <HStack background="background" height="100vh" overflow="hidden">
          <LeftNav />
          <VStack
            width="100%"
            height="100vh"
            overflow="auto"
            flexGrow={1}
            className="main-container"
          >
            <TopNav />
            <CloudErrorBoundary name="RouterSwitch">
              <TransitionGroup>
                <CSSTransition
                  timeout={350}
                  onEntering={(): void => {
                    window.scrollTo(0, 0);
                  }}
                >
                  <OAuthInfoProvider>
                    <HStack width="100%" justifyContent="flex-start">
                      <RouterSwitch>
                        <Route path={[AppRoute.Projects.Overview, AppRoute.Projects.Home]}>
                          <Projects />
                        </Route>
                        <Route exact path={AppRoute.Home}>
                          <Redirect to={AppRoute.Projects.Home} />
                        </Route>

                        <Route exact path={AppRoute.Access.OAuthCreate}>
                          <OAuthCreateClient />
                        </Route>
                        <Route exact path={AppRoute.Access.OAuthEdit}>
                          <OAuthEditClient />
                        </Route>
                        <Route exact path={AppRoute.Access.Permissions}>
                          <PermissionsPage />
                        </Route>

                        {isLegacyAPIManagementEnabled && (
                          <Route exact path={AppRoute.Access.LegacyAPI}>
                            <LegacyTokensProvider>
                              <LegacyAPITokenManagement />
                            </LegacyTokensProvider>
                          </Route>
                        )}
                        <Route path={AppRoute.Projects.Home}>
                          <Projects />
                        </Route>
                        <Route exact path={AppRoute.Home}>
                          <Redirect to={AppRoute.Projects.Overview} />
                        </Route>
                        <Route path={AppRoute.Products.Onramp}>
                          <OnrampLanding />
                        </Route>
                        <Route path={AppRoute.Products.WalletFunding}>
                          <WalletFunding />
                        </Route>
                        <Route path={AppRoute.Products.PlatformWallets}>
                          <PlatformWallets />
                        </Route>
                        {isOnStakingAllowlist && (
                          <Route path={AppRoute.Products.Staking}>
                            <Staking />
                          </Route>
                        )}
                        <Route exact path={AppRoute.Access.OAuthCreate}>
                          <OAuthCreateClient />
                        </Route>
                        <Route exact path={AppRoute.Access.OAuthEdit}>
                          <OAuthEditClient />
                        </Route>
                        {/* Placeholder for the cookie preferences dialog. */}
                        <Route exact path={AppRoute.CookiePreferences} />
                        <Route path={AppRoute.Products.AdvancedTrade}>
                          <AdvancedTradeLanding />
                        </Route>
                        <Route path={AppRoute.Products.Commerce}>
                          <CommerceLanding />
                        </Route>
                        <Route exact path={AppRoute.Products.Wallets}>
                          <WalletsLanding />
                        </Route>
                        <Route path={AppRoute.Products.BundlerAndPaymaster}>
                          <BundlerAndPaymasterLanding />
                        </Route>

                        {isBaseL3Enabled && (
                          <Route exact path={AppRoute.Products.BaseL3}>
                            <BaseL3Landing />
                          </Route>
                        )}
                        {isBaseL3Enabled && (
                          <Route path={AppRoute.Products.BaseL3Deploy}>
                            <BaseL3Deployment />
                          </Route>
                        )}
                        {isWalletHistoryEnabled && (
                          <Route path={[AppRoute.Products.OnchainData]}>
                            <OnChainData />
                          </Route>
                        )}
                        <Route path={AppRoute.Products.Onramp}>
                          <OnrampLanding />
                        </Route>
                        <Route path={AppRoute.Products.PlatformWallets}>
                          <PlatformWallets />
                        </Route>
                        <Route path={AppRoute.Products.Base}>
                          <BaseLanding />
                        </Route>
                        <Route exact path={AppRoute.Legal.BaseNodeTOS}>
                          <BaseNodeTermsOfService />
                        </Route>
                        <Route path={AppRoute.Products.Faucet}>
                          <Faucet />
                        </Route>
                        <Route path={AppRoute.Products.Boat}>
                          {isOnBOATAllowlist ? (
                            <TemplateLanding />
                          ) : (
                            <Redirect to={AppRoute.Home} />
                          )}
                        </Route>
                        <Route path={AppRoute.Products.Exchange}>
                          <ExchangeLanding />
                        </Route>
                        <Route exact path={[AppRoute.Products.Verifications]}>
                          <VerificationsLanding />
                        </Route>
                        <Route exact path={AppRoute.Products.App}>
                          <CoinbaseAppLanding />
                        </Route>
                        <Route exact path={AppRoute.Products.Participate}>
                          <Redirect to={AppRoute.Participate.Home} />
                        </Route>
                        {isOnParticipateAllowlist && (
                          <Route exact path={AppRoute.Participate.Home}>
                            <NewClusterProvider>
                              <ParticipateDashboard />
                            </NewClusterProvider>
                          </Route>
                        )}
                        {isOnParticipateAllowlist && (
                          <Route path={AppRoute.Participate.Create}>
                            <CreateCluster />
                          </Route>
                        )}
                        {isOnParticipateAllowlist && (
                          <Route
                            exact
                            path={[
                              AppRoute.Participate.DetailsByClusterName,
                              AppRoute.Participate.DetailsByClusterNameAndTabName,
                            ]}
                          >
                            {/* Create stacking context that keeps CDS TabNavigation underneath the top nav bar */}
                            <Box zIndex={zIndex.interactable} position="relative">
                              <ClusterDetailsRoute />
                            </Box>
                          </Route>
                        )}
                        <Route path="*">
                          <Redirect to={AppRoute.Home} />
                        </Route>
                      </RouterSwitch>
                    </HStack>
                  </OAuthInfoProvider>
                </CSSTransition>
              </TransitionGroup>
            </CloudErrorBoundary>
            {/* add spacer to prevent content from being cutoff by mobile navbar */}
            {isPhone && <Divider height={60} />}
            <Spacer />
            {hasClosedDiscordBanner ? <HiringBanner /> : <DiscordBanner />}
          </VStack>
        </HStack>
      </NavigationProvider>
    </>
  );
}

function AppRouterWrapper() {
  const isBuildQuestEnabled = useIsBuildQuestEnabled();

  const { activeOrg } = useGetUser();
  const { oldestProjectCreatedByUser } = useGetProjects(activeOrg?.organizationId);

  useEffect(() => {
    if (oldestProjectCreatedByUser) {
      const daysSinceCreation = getDaysSinceDate(oldestProjectCreatedByUser);

      if (daysSinceCreation >= 2) {
        triggerResearchEventOnUserAction('CDP-Portal-User-Survey');
      }
    }
  }, [oldestProjectCreatedByUser]);

  return (
    <Router history={history}>
      <RouterSwitch>
        <Route path={AppRoute.CDPQuest.Home}>
          {isBuildQuestEnabled ? <BuildQuestLanding /> : <Redirect to={AppRoute.Home} />}
        </Route>
        <AppRouter />
      </RouterSwitch>
    </Router>
  );
}

function AuthenticatedApp() {
  return (
    <QueryClientProviderWrapper>
      <EnvBanner />
      <ClientKillSwitchProvider
        initialKillSwitches={defaultClientKillSwitchConfig}
        scope={config.appAnalyticsName}
      >
        <ExperimentsProvider>
          <FeatureFlagProvider>
            <ProjectProvider>
              <AppRouterWrapper />
              <ReactQueryDevtools initialIsOpen={false} />
            </ProjectProvider>
          </FeatureFlagProvider>
        </ExperimentsProvider>
      </ClientKillSwitchProvider>
    </QueryClientProviderWrapper>
  );
}

type AppProps = {
  locale: string;
  messages?: Record<string, string>;
};

export function App({ locale, messages }: AppProps) {
  useEffect(() => {
    initKillSwitch();
    initSprig();
  }, []);

  const clientId = process.env.TWO_FACTOR_CLIENT_ID;
  if (!clientId) throw new Error(`Could not read 2FA clientId from env`);

  const handleBoundaryError = useCallback((boundaryError: TwoFactorBoundaryError) => {
    const { error, errorInfo, context, details } = boundaryError;

    logError(
      error.message,
      {
        context,
      },
      {
        name: 'API Key Creation Two Factor Error',
        error: error.message,
        errorInfo: errorInfo.componentStack,
        details: details?.toString(),
      },
    );
  }, []);

  return (
    <StrictMode>
      <AppStateProvider>
        <CloudThemeProvider>
          <CBErrorVitalsClientContextProvider value={errorVitalsClientContext}>
            <CloudErrorBoundary name="AuthenticatedApp">
              <CookieProvider>
                <IntlProvider locale={locale} defaultLocale="en" messages={messages}>
                  <SessionProvider fallback={<UnauthenticatedApp />}>
                    <TwoFactorGlobalConfigProvider
                      clientId={clientId || ''}
                      target={getTwoFactorTargetEnv()}
                      onBoundaryError={handleBoundaryError}
                    >
                      <AuthenticatedApp />
                    </TwoFactorGlobalConfigProvider>
                  </SessionProvider>
                </IntlProvider>
              </CookieProvider>
            </CloudErrorBoundary>
          </CBErrorVitalsClientContextProvider>
        </CloudThemeProvider>
      </AppStateProvider>
    </StrictMode>
  );
}
